import { useCallback, useRef, useState } from 'react';
import type { EditorSnapshot } from '../types/editor';

type HistoryState = {
  undoStack: EditorSnapshot[];
  redoStack: EditorSnapshot[];
  current: EditorSnapshot | null;
};

export function useHistory( maxSnapshots = 50 ) {
  const [ state, setState ] = useState<HistoryState>({
    undoStack: [],
    redoStack: [],
    current: null
  });

  // Use ref to avoid stale closures in callbacks
  const stateRef = useRef( state );
  stateRef.current = state;

  const pushSnapshot = useCallback(( snapshot: EditorSnapshot ) => {
    setState(( prev ) => {
      const undoStack = prev.current
        ? [ ...prev.undoStack, prev.current ].slice( -maxSnapshots )
        : prev.undoStack;

      return {
        undoStack,
        redoStack: [],
        current: snapshot
      };
    });
  }, [maxSnapshots]);

  const setCurrent = useCallback(( snapshot: EditorSnapshot ) => {
    setState(( prev ) => ({
      ...prev,
      current: snapshot
    }));
  }, []);

  const undo = useCallback(( currentSnapshot: EditorSnapshot ): EditorSnapshot | null => {
    const prev = stateRef.current;
    if ( prev.undoStack.length === 0 ) return null;

    const newUndo = [...prev.undoStack];
    const restored = newUndo.pop()!;
    const newRedo = [ ...prev.redoStack, currentSnapshot ];

    setState({
      undoStack: newUndo,
      redoStack: newRedo,
      current: restored
    });

    return restored;
  }, []);

  const redo = useCallback(( currentSnapshot: EditorSnapshot ): EditorSnapshot | null => {
    const prev = stateRef.current;
    if ( prev.redoStack.length === 0 ) return null;

    const newRedo = [...prev.redoStack];
    const restored = newRedo.pop()!;
    const newUndo = [ ...prev.undoStack, currentSnapshot ];

    setState({
      undoStack: newUndo,
      redoStack: newRedo,
      current: restored
    });

    return restored;
  }, []);

  const canUndo = state.undoStack.length > 0;
  const canRedo = state.redoStack.length > 0;

  return { pushSnapshot, undo, redo, canUndo, canRedo, setCurrent };
}
